import { TbPercentage, TbCalendar, TbCurrencyDollar } from "react-icons/tb";
import { FiShoppingCart, FiCheck, FiX } from "react-icons/fi";

const CartButton = ({
  quotation,
  isInCart,
  isPaidInCart,
  isSampleSelected,
  isFullOrderSelected,
  onAddToCart,
  onRemoveFromCart,
  cartLoading
}) => {
  if (isFullOrderSelected) {
    return (
      <button
        disabled
        className="flex items-center justify-center gap-1.5 px-4 py-2 bg-orange-50 border border-orange-200 rounded-lg font-semibold text-xs text-orange-600 cursor-default"
      >
        <FiCheck className="text-sm" />
        Full Order
      </button>
    );
  }

  if (isSampleSelected) {
    return (
      <button
        disabled
        className="flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg font-semibold text-xs text-emerald-600 cursor-default"
      >
        <FiCheck className="text-sm" />
        {isPaidInCart ? "Paid" : "Selected"}
      </button>
    );
  }

  if (isInCart) {
    return (
      <button
        onClick={() => onRemoveFromCart(quotation)}
        disabled={cartLoading}
        className="flex items-center justify-center gap-1.5 px-4 py-2 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 font-semibold text-xs text-red-600"
      >
        {cartLoading ? (
          <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
        ) : (
          <FiX className="text-sm" />
        )}
        Remove
      </button>
    );
  }

  return (
    <button
      onClick={() => onAddToCart(quotation)}
      disabled={cartLoading}
      className="flex items-center justify-center gap-2 px-4 py-2 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
    >
      {cartLoading ? (
        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
      ) : (
        <FiShoppingCart className="text-sm" />
      )}
      Add to Cart
    </button>
  );
};

const QuotationSummaryCard = ({
  q,
  order,
  onAddToCart,
  onRemoveFromCart,
  sampleCart,
  cartQuotationIds,
  cartLoading,
  isSampleSelected,
  isFullOrderSelected,
  isPaidInCart
}) => {
  // Helper to get match percentage styling
  const getMatchStyle = (percentage) => {
    if (percentage >= 80) return "bg-green-50 text-green-700 border-green-200";
    if (percentage >= 60) return "bg-blue-50 text-blue-700 border-blue-200";
    if (percentage >= 40) return "bg-yellow-50 text-yellow-700 border-yellow-200";
    return "bg-red-50 text-red-700 border-red-200";
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* HEADER SECTION */}
      <div className="bg-gradient-to-r from-primaryColor/5 to-primaryColor/10 p-4 sm:p-5 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Left: ID & Date */}
          <div className="grid grid-cols-2 gap-4 flex-wrap">
            <div>
              <p className="text-xs text-gray-500 mb-1">Quotation ID</p>
              <p className="font-bold text-primaryColor text-sm sm:text-base">
                #{q.quotationUniqueNumber}
              </p>
            </div>
            <div className="hidden sm:block w-px h-10 bg-gray-300" />
            <div>
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <TbCalendar className="text-sm" /> Submitted
              </p>
              <p className="text-sm font-medium text-gray-700">
                {new Date(q.createdAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric"
                })}
              </p>
            </div>
          </div>

          {/* Right: Status & Match & Cart Button */}
          <div className="grid grid-cols-3 gap-3 items-center">
            {/* Status Badge */}
            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-500 hidden sm:block">Status:</p>
              <span className={`text-xs px-3 py-1.5 rounded-full font-semibold inline-flex items-center gap-1
          ${q.review?.status === "Approved"
                  ? "bg-green-100 text-green-700 border border-green-200"
                  : q.review?.status === "Rejected"
                    ? "bg-red-100 text-red-700 border border-red-200"
                    : "bg-yellow-100 text-yellow-700 border border-yellow-200"
                }`}>
                {q.review?.status === "Approved" && (
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
                {q.review?.status}
              </span>
            </div>

            {/* Match Percentage */}
            <div className={`px-4 py-2 rounded-lg border-2 ${getMatchStyle(q.matchingPercentage)}`}>
              <p className="text-[10px] font-medium opacity-75 mb-0.5 flex items-center gap-1">
                <TbPercentage className="text-xs" /> MATCH
              </p>
              <p className="text-lg sm:text-xl font-bold leading-none">
                {q.matchingPercentage?.toFixed(1)}%
              </p>
            </div>

            {/* Cart Button - NEW */}
            {onAddToCart && (
              <CartButton
                quotation={q}
                isInCart={cartQuotationIds?.has(q._id?.toString())}
                isPaidInCart={isPaidInCart}
                isSampleSelected={isSampleSelected}
                isFullOrderSelected={isFullOrderSelected}
                onAddToCart={onAddToCart}
                onRemoveFromCart={onRemoveFromCart}
                cartLoading={cartLoading}
              />
            )}
          </div>
        </div>
      </div>

      {/* MAIN DETAILS GRID */}
      <div className="p-4 sm:p-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Price */}
          <div className="bg-gradient-to-br from-primaryColor/5 to-primaryColor/10 p-3 sm:p-4 rounded-lg border border-primaryColor/20 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <TbCurrencyDollar className="text-primaryColor text-lg" />
              <p className="text-xs text-gray-500 font-medium">Price</p>
            </div>
            <p className="font-bold text-primaryColor text-base sm:text-lg">
              {q.currency} {q.pricing?.commissionAdjustedAmount || "N/A"}
            </p>
            <p className="text-[10px] text-gray-500 mt-1">per {q.standardizedUnit}</p>
          </div>

          {/* Quantity */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 sm:p-4 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <p className="text-xs text-gray-600 font-medium">Quantity</p>
            </div>
            <p className="font-bold text-gray-800 text-base sm:text-lg">
              {q.quantity?.volume}
            </p>
            <p className="text-[10px] text-gray-500 mt-1">{q.standardizedUnit}</p>
          </div>

          {/* Availability */}
          <div className="bg-gradient-to-br from-green-50 to-green-100/50 p-3 sm:p-4 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-gray-600 font-medium">Availability</p>
            </div>
            <p className={`font-bold text-base sm:text-lg ${q.quantity?.isAvailableFull ? "text-green-700" : "text-orange-600"}`}>
              {q.quantity?.isAvailableFull ? "Full" : "Partial"}
            </p>
            <p className="text-[10px] text-gray-500 mt-1">
              {q.quantity?.isAvailableFull ? "In stock" : "Limited"}
            </p>
          </div>

          {/* Batch Number */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 p-3 sm:p-4 rounded-lg border border-purple-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <p className="text-xs text-gray-600 font-medium">Batch ID</p>
            </div>
            <p className="font-bold text-gray-800 text-xs sm:text-sm break-all">
              {q.batch_Lot_Number}
            </p>
            <p className="text-[10px] text-gray-500 mt-1">Lot number</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationSummaryCard;